'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { MODULE_COUNT } from './steps';

/**
 * WHICH SURFACE the Labs screens are running on.
 *
 * The 18 screens were written for exactly one host: the gated sprint inside the platform shell, behind
 * the paywall, measured against an experiment arm. Two of them reach past their own props to ask the
 * host a question, `StepShell` asks "how many modules are there" (for the progress bar) and, via
 * `useLabsTrialBar`, "should I reserve a row for the trial bar". Both answers were constants.
 *
 * The public SEO preview (`app/try/[vertical]`) needs different answers to both: it runs two modules,
 * not four, and it must never show a trial bar to an anonymous visitor who has no account to upgrade.
 *
 * 🔴 THE DEFAULTS ARE THE SPRINT'S CURRENT BEHAVIOUR, EXACTLY. There is no provider anywhere in the
 * gated sprint, so `useLabsSurface()` returns {@link SPRINT_SURFACE} there, `moduleCount` is the same
 * `MODULE_COUNT` the shell used to import directly, and `trialBar` is true, which leaves the trial bar's
 * own `needsPayment` / step logic as the only thing deciding it. Finance and Data are live mid-experiment
 * (Experiment 3, enrolled 2026-08-24), so anything less than a byte-identical default is not acceptable
 * here: this file must be readable as "nothing changed" by anyone auditing that experiment.
 *
 * Deliberately NOT folded into `PackProvider`. A pack is CONTENT (what a vertical says); this is HOST
 * (where the screens are running). The same consulting pack renders on both surfaces, and collapsing the
 * two would mean a pack had to know which page it was on.
 */

export interface LabsSurface {
  /**
   * How many modules this surface's flow runs, i.e. the denominator of the progress bar. The gated
   * sprint runs all four; the preview runs M1 and M2 only, so its `m2-results` is 2-of-2 and reads
   * 100% rather than the 50% it would show against the sprint's four.
   */
  moduleCount: number;
  /**
   * Whether this surface may render the in-sprint trial bar at all.
   *
   * False is a HARD veto, not a preference: `useNeedsPayment` returns true for a signed-out visitor
   * (no Clerk user → no `subscription_status` → "needs payment"), so on a public route the bar would
   * otherwise appear for every anonymous reader and send them to `/payment` with no account.
   */
  trialBar: boolean;
}

/** The gated sprint's answers, and the value every existing caller gets, since it has no provider. */
export const SPRINT_SURFACE: LabsSurface = {
  moduleCount: MODULE_COUNT,
  trialBar: true
};
const SurfaceContext = createContext<LabsSurface>(SPRINT_SURFACE);
export function LabsSurfaceProvider(t0) {
  const $ = _c(3);
  const {
    children,
    surface
  } = t0;
  const value = surface;
  let t1;
  if ($[0] !== children || $[1] !== value) {
    t1 = <SurfaceContext.Provider value={value}>{children}</SurfaceContext.Provider>;
    $[0] = children;
    $[1] = value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

/**
 * The active surface. Unlike `useLabsRuntime`, this NEVER throws with no provider: a screen with no
 * pack has no content and must fail loudly, but a screen with no surface has a correct default, the
 * sprint, and failing there would break the live verticals to serve a new one.
 */
export function useLabsSurface() {
  return useContext(SurfaceContext);
}